import { ChevronDownIcon } from "@primer/octicons-react";
import { Button, SelectPanel } from '@primer/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCommonContext } from '../../contexts/common';
import { useCommonSelectedHook } from '../../hooks/useCommonSelectedHook';
import { loaiThuocApi } from '../../api/loaiThuocApi';
import { khoThuocApi } from '../../api/khoThuocApi';
import { ts_hocsinh_lichsu_yte_loaithuoc } from '../../models/response/category/ts_hocsinh_lichsu_yte_loaithuoc';
import { KhoThuocTonKhoItemResponse } from '../../models/response/category/KhoThuocTonKhoItemResponse';
import { NotifyHelper } from '../../helpers/toast';

type ComboboxLoaiThuocByIdProps = {
    isReadonly?: boolean;
    value?: string;
    onValueChanged: (value: string, data?: ts_hocsinh_lichsu_yte_loaithuoc) => void;
    className?: string;
    isShowClearButton?: boolean;
    preText?: string;
    width?: string | number;
    stylingMode?: 'outlined' | 'filled' | 'underlined';
};

export const ComboboxLoaiThuocById = (props: ComboboxLoaiThuocByIdProps) => {
    const [loaiThuocList, setLoaiThuocList] = useState<ts_hocsinh_lichsu_yte_loaithuoc[]>([]);
    const [tonKhoList, setTonKhoList] = useState<KhoThuocTonKhoItemResponse[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const { translate } = useCommonContext();
    const { dm_coso_id } = useCommonSelectedHook();

    const loadLoaiThuoc = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await loaiThuocApi.selectAll();
            if (response.is_success) {
                // Lọc theo dm_coso_id
                const filteredData = (response.data || []).filter((item: ts_hocsinh_lichsu_yte_loaithuoc) => item.dm_coso_id === dm_coso_id);
                setLoaiThuocList(filteredData);
            } else {
                NotifyHelper.Error("Có lỗi khi tải danh sách loại thuốc");
            }
        } catch (error: any) {
            NotifyHelper.Error(error.message || "Có lỗi khi tải danh sách loại thuốc");
        } finally {
            setIsLoading(false);
        }
    }, [dm_coso_id]);

    const loadTonKho = useCallback(async () => {
        try {
            const response = await khoThuocApi.selectTonKho();
            if (response.is_success) {
                // Lọc theo dm_coso_id
                const filteredData = (response.data || []).filter((item: KhoThuocTonKhoItemResponse) => item.dm_coso_id === dm_coso_id);
                setTonKhoList(filteredData);
            }
        } catch (error: any) {
            console.error('Error loading ton kho:', error);
        }
    }, [dm_coso_id]);

    useEffect(() => {
        loadLoaiThuoc();
        loadTonKho();
    }, [dm_coso_id, loadLoaiThuoc, loadTonKho]);

    const dataSource = useMemo(() => {
        return loaiThuocList.map(x => {
            // Tìm tồn kho cho loại thuốc này
            const tonKho = tonKhoList.find(tk => tk.ts_hocsinh_lichsu_yte_loaithuoc_id === x.id);

            return {
                id: x.id.toString(),
                text: x.ten_thuoc + (x.ten_thuoc_en ? ` (${x.ten_thuoc_en})` : "") + ` (Tồn: ${tonKho?.ton_kho || 0})`,
                data: x,
                tonKho: tonKho?.ton_kho || 0
            };
        });
    }, [loaiThuocList, tonKhoList]);

    const filteredItems = useMemo(() => {
        return dataSource.filter(item => item.text.toLowerCase().includes(filter.toLowerCase()));
    }, [dataSource, filter]);

    const selected = useMemo(() => {
        return dataSource.find(x => x.id === props.value);
    }, [dataSource, props.value]);

    const setSelected = (selecteds: any) => {
        if (selecteds) {
            const selectedData = loaiThuocList.find(x => x.id.toString() === selecteds.id);
            props.onValueChanged(selecteds.id, selectedData);
        } else {
            props.onValueChanged("");
        }
    };

    return (
        <SelectPanel
            renderAnchor={({ children, 'aria-labelledby': ariaLabelledBy, ...anchorProps }) => (
                <Button
                    trailingAction={ChevronDownIcon}
                    aria-labelledby={` ${ariaLabelledBy}`}
                    {...anchorProps}
                    disabled={props.isReadonly || isLoading}
                    sx={{
                        width: '100%',
                        '[data-component="buttonContent"]': {
                            display: 'flex',
                            justifyContent: 'flex-start',
                            gridTemplateAreas: 'none',
                            gridTemplateColumns: 'none',
                            '& > :first-child': {
                                flex: 1,
                                textAlign: 'left'
                            }
                        }
                    }}
                >
                    {children || translate("Chọn loại thuốc")}
                </Button>
            )}
            title={""}
            placeholderText="Search"
            open={open}
            onOpenChange={setOpen}
            items={filteredItems}
            selected={selected}
            onSelectedChange={setSelected}
            onFilterChange={setFilter}
            showItemDividers={true}
            overlayProps={{ width: 'large', height: 'medium' }}
        />
    );
};
